import VideoMeetingPage from '@modules/zoom/components/VideoMeeting/VideoMeeting';
import VideoWaitingRoomPage from '@modules/zoom/components/VideoWaitingRoom/VideoWaitingRoom';
import { ZoomContext } from '@modules/zoom/contexts/ZoomContext';
import ZoomVideo, { Stream } from '@zoom/videosdk';
import { useGetMeetingDetails, useGetVideoSettings } from 'apps/agora/src/actions';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import useMeetingWebSocket from './useMeetingWebsocket';

const VideoMeeting = () => {
  const [zoomClient] = useState(() => ZoomVideo.createClient());
  const [isWaitingRoom, setIsWaitingRoom] = useState(true);
  const { meetingId } = useParams<{ meetingId: string }>();
  const [activeCamera, setActiveCamera] = useState<string | undefined>();
  const [activeMicrophone, setActiveMicrophone] = useState<string | undefined>();
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isMeetingLoading, setIsMeetingLoading] = useState(false);
  const [canJoinMeeting, setCanJoinMeeting] = useState(false);
  const [isZoomClientReady, setIsZoomClientReady] = useState(false);
  const [stream, setStream] = useState<typeof Stream | undefined>();

  const history = useHistory();

  const toast = useToast();

  const { data: meetingDetails } = useGetMeetingDetails(meetingId, {
    onError: (err: any) => {
      history.replace('/home');
      toast.error(
        err?.response?.data?.error?.message ?? err?.message ?? 'Meeting is not available.'
      );
    },
  });

  const { data: videoSettings } = useGetVideoSettings();

  const { webSocket, isSocketConnected, closeWebsocketConnection } = useMeetingWebSocket();

  const logError = (error: any) => {
    try {
      if (!webSocket || !zoomClient) return;

      let errorType = 'Unknown';
      let errorMessage = 'An unknown error occurred';
      let stackTrace = 'No stack trace available';

      if (error instanceof Error) {
        errorType = error.name || 'Error';
        errorMessage = error.message || 'An unknown error occurred';
        stackTrace = error.stack || 'No stack trace available';
      } else if (typeof error === 'string') {
        errorType = 'StringError';
        errorMessage = error;
      } else if (typeof error === 'object' && error !== null) {
        errorType = error.constructor?.name || 'ObjectError';
        errorMessage = JSON.stringify(error, null, 2);
      } else {
        errorType = typeof error;
        errorMessage = String(error);
      }

      // Collect diagnostic information
      const diagnostics = {
        browser: {
          userAgent: window.navigator.userAgent,
          platform: window.navigator.platform,
          language: window.navigator.language,
        },
        devices: {
          activeCamera,
          activeMicrophone,
        },
        mediaState: {
          isCameraActive,
          isMicrophoneActive,
        },
        sessionInfo: {
          userId: zoomClient?.getSessionInfo()?.userId,
          sessionId: zoomClient?.getSessionInfo()?.sessionId,
        },
      };

      webSocket.send(
        JSON.stringify({
          type: 'error-log',
          meetingId,
          username: zoomClient?.getSessionInfo()?.userName ?? 'Unknown User',
          errorType,
          errorMessage,
          stackTrace,
          diagnostics,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (loggingError) {
      console.error('Error logging error:', loggingError);
    }
  };

  const contextValue = {
    stream,
    zoomClient,
    videoSettings: videoSettings || {},
    meetingDetails,
    activeCamera,
    activeMicrophone,
    isMicrophoneActive,
    isCameraActive,
    isMeetingLoading,
    canJoinMeeting,
    isZoomClientReady,
    webSocket,
    isSocketConnected,
    setActiveCamera,
    setActiveMicrophone,
    setIsMicrophoneActive,
    setIsCameraActive,
    setIsMeetingLoading,
    setCanJoinMeeting,
    setIsZoomClientReady,
    setStream,
    closeWebsocketConnection,
    logError,
  };

  return (
    <ZoomContext.Provider value={contextValue}>
      {isWaitingRoom ? (
        <VideoWaitingRoomPage setIsWaitingRoom={setIsWaitingRoom} />
      ) : (
        <VideoMeetingPage />
      )}
    </ZoomContext.Provider>
  );
};

export default VideoMeeting;
